import config from "../../config/config";
import * as api from "./packages.api";
import * as mocks from "./packages.mocks";
import { CreatePackageInputs, Package, UpdatePackageInputs, CreatePromotionInput, UpdatePromotionInput, Promotion } from "./packages.interfaces";

// Mock para obter promoções
const mockGetPromotions = async (packageId: string): Promise<Promotion[]> => {
    console.log(`Mock: Getting promotions for package ${packageId}`);
    
    // Descobrir a qual coleção o ID pertence consultando todos os pacotes mock
    
    // Método 1: Se o ID corresponder a um formato específico
    const packageIdNumber = packageId.split('-')[1];
    if (packageIdNumber === '1' && mocks.mockPromotions.basic) {
        return mocks.mockPromotions.basic;
    } else if (packageIdNumber === '2' && mocks.mockPromotions.intermediate) {
        return mocks.mockPromotions.intermediate;
    } else if (packageIdNumber === '3' && mocks.mockPromotions.advanced) {
        return mocks.mockPromotions.advanced;
    }
    
    // Método 2: Se não corresponder, retornar qualquer promoção do mock
    // No modo de simulação, vamos retornar a primeira coleção disponível
    for (const packageType of ['basic', 'intermediate', 'advanced']) {
        if (mocks.mockPromotions[packageType] && mocks.mockPromotions[packageType].length > 0) {
            console.log(`Mock: Returning promotions from ${packageType} collection`);
            return mocks.mockPromotions[packageType];
        }
    }
    
    console.log(`Mock: No promotions found for package ${packageId}`);
    return [];
};

// Mock para atualizar promoção
const mockUpdatePromotion = async (packageId: string, promotionId: string, data: UpdatePromotionInput): Promise<void> => {
    console.log("MOCK function called: mockUpdatePromotion");
    console.log(`Mock: Trying to update promotion ${promotionId} in package ${packageId}`);
    console.log(`Mock: Update data:`, data);
    
    // Tentar atualizar em todas as coleções de pacotes
    const packageTypes = ['basic', 'intermediate', 'advanced'];
    let updated = false;
    
    for (const packageType of packageTypes) {
        if (mocks.mockPromotions[packageType]) {
            // Encontrar e atualizar a promoção no array de mocks
            const promotionIndex = mocks.mockPromotions[packageType].findIndex(
                (promo) => promo.id === promotionId
            );
            
            if (promotionIndex !== -1) {
                // Atualizar os dados da promoção
                mocks.mockPromotions[packageType][promotionIndex] = {
                    ...mocks.mockPromotions[packageType][promotionIndex],
                    ...data
                };
                
                console.log(`Mock: Promotion ${promotionId} updated successfully in ${packageType}`);
                updated = true;
                break;
            }
        }
    }
    
    if (!updated) {
        console.log(`Mock: Promotion ${promotionId} not found in any package type`);
    }
    
    return Promise.resolve();
};

// Mock functions for package modules
const mockGetPackageModules = async (packageId: string): Promise<any[]> => {
    console.log(`Mock: Getting modules for package ${packageId}`);
    return [
        { id: '1', name: 'Module 1', order: 1 },
        { id: '2', name: 'Module 2', order: 2 },
    ];
};

const mockSyncPackageModules = async (packageId: string, syncData: any): Promise<any> => {
    console.log(`Mock: Syncing modules for package ${packageId}`, syncData);
    return { success: true, syncedCount: 2 };
};

const mockDeletePackageModule = async (packageId: string, moduleId: string): Promise<void> => {
    console.log(`Mock: Deleting module ${moduleId} from package ${packageId}`);
    return Promise.resolve();
};

// Mock para adicionar módulo ao pacote
const mockAddPackageModule = async (packageId: string, moduleId: string): Promise<any> => {
    console.log(`Mock: Adding module ${moduleId} to package ${packageId}`);
    return { success: true, addedModuleId: moduleId };
};

// Exported service functions
export const getPromotions = async (packageId: string): Promise<Promotion[]> => {
    return await (config.useRealApi ? api.apiGetPromotions(packageId) : mockGetPromotions(packageId));
};

export const getPackages = async (page?: number, limit?: number): Promise<Package[]> => {
    return await (config.useRealApi ? api.apiGetPackages(page, limit) : mocks.getPackages(page, limit));
};

export const getPackage = async (id: string): Promise<Package> => {
    return await (config.useRealApi ? api.apiGetPackage(id) : mocks.getPackage(id));
};

export const createPackage = async (data: CreatePackageInputs): Promise<void> => {
    return await (config.useRealApi ? api.apiCreatePackage(data) : mocks.createPackage(data));
};

export const updatePackage = async (id: string, data: UpdatePackageInputs): Promise<void> => {
    return await (config.useRealApi ? api.apiUpdatePackage(id, data) : mocks.updatePackage(id, data));
};

export const deletePackage = async (ids: string[]): Promise<void> => {
    return await (config.useRealApi ? api.apiDeletePackage(ids) : mocks.deletePackage(ids));
};

export const getPackageModules = async (packageId: string): Promise<any[]> => {
    return await (config.useRealApi ? api.apiGetPackageModules(packageId) : mockGetPackageModules(packageId));
};

export const syncPackageModules = async (packageId: string, syncData: any): Promise<any> => {
    return await (config.useRealApi ? api.apiSyncPackageModules(packageId, syncData) : mockSyncPackageModules(packageId, syncData));
};

export const deletePackageModule = async (packageId: string, moduleId: string): Promise<void> => {
    return await (config.useRealApi ? api.apiDeletePackageModule(packageId, moduleId) : mockDeletePackageModule(packageId, moduleId));
};

export const addPackageModule = async (packageId: string, moduleId: string): Promise<any> => {
    return await (config.useRealApi ? api.apiAddPackageModule(packageId, moduleId) : mockAddPackageModule(packageId, moduleId));
};

export const batchAddPackageModules = async (packageId: string, moduleIds: string[]): Promise<any[]> => {
    return await (config.useRealApi ? api.apiBatchAddPackageModules(packageId, moduleIds) : Promise.resolve([]));
};

export const batchDeletePackageModules = async (packageId: string, moduleIds: string[]): Promise<void> => {
    return await (config.useRealApi ? api.apiBatchDeletePackageModules(packageId, moduleIds) : Promise.resolve());
};

export const createPromotion = async (packageId: string, data: CreatePromotionInput): Promise<string> => {
    return await (config.useRealApi ? api.apiCreatePromotion(packageId, data) : mocks.createPromotion(packageId, data));
};

export const updatePromotion = async (
    packageId: string, 
    promotionId: string, 
    data: UpdatePromotionInput
): Promise<void> => {
    return await (config.useRealApi ? api.apiUpdatePromotion(packageId, promotionId, data) : mockUpdatePromotion(packageId, promotionId, data));
};

export const deletePromotion = async (packageId: string, promotionId: string): Promise<void> => {
    return await (config.useRealApi ? api.apiDeletePromotion(packageId, promotionId) : mocks.deletePromotion(packageId, promotionId));
};
